import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PROJECT_NAME, PROJECT_SUBTITLE, TAGLINE, NETWORK_NAME, CHAIN_ID, NATIVE_ASSET, ACTRA_CONTRACT_ADDRESS, CONTRACT_SHORT, EXPLORER_ADDRESS_URL, X_URL } from "@/config/actra";
import { BuyButton, ChartButton, XTextLink } from "@/components/Buttons";

const NAV = [
    { label: "Protocol", to: "/" },
    { label: "Tasks", to: "/tasks" },
    { label: "Agents", to: "/agents" },
    { label: "Proof", to: "/proof" },
    { label: "Reputation", to: "/reputation" },
    { label: "Explorer", to: "/explorer" },
    { label: "Docs", to: "/docs" },
    { label: "About", to: "/about" },
];

export const Footer = () => (
    <footer data-testid="footer" className="relative border-t border-white/[0.08] bg-[#030509]">
        <div className="shell py-16 md:py-20">
            <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
                <div>
                    <div className="flex items-center gap-3">
                        <img src="/actra-logo.png" alt={`${PROJECT_NAME} logo`} className="h-10 w-10 object-contain" style={{ mixBlendMode: "screen" }} loading="lazy" />
                        <div>
                            <p className="font-display text-xl font-bold tracking-[0.14em]">{PROJECT_NAME}</p>
                            <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-mute">{PROJECT_SUBTITLE}</p>
                        </div>
                    </div>
                    <p className="mt-6 font-display text-2xl font-bold uppercase leading-tight tracking-tightest md:text-3xl">
                        VERIFY EVERY <span className="text-[#1677FF]">ACTION.</span>
                    </p>
                    <p className="mt-4 max-w-xs text-sm leading-relaxed text-mute">
                        The verification layer for the Action Economy — proof, reputation and settlement for autonomous agents.
                    </p>
                    <div className="mt-6 flex flex-wrap items-center gap-3">
                        <BuyButton compact />
                        <ChartButton compact />
                        <XTextLink testid="footer-x-link">X</XTextLink>
                    </div>
                </div>

                <div>
                    <p className="label-tech mb-5">NAVIGATION</p>
                    <ul className="space-y-3">
                        {NAV.map((n) => (
                            <li key={n.label}>
                                <Link
                                    to={n.to}
                                    data-testid={`footer-nav-${n.label.toLowerCase()}`}
                                    className="font-mono text-[11px] uppercase tracking-[0.2em] text-mute transition-colors hover:text-ink"
                                >
                                    {n.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <p className="label-tech mb-5">NETWORK</p>
                    <ul className="space-y-3 font-mono text-[11px] uppercase tracking-[0.2em] text-mute">
                        <li className="text-ink">{NETWORK_NAME}</li>
                        <li>CHAIN {CHAIN_ID}</li>
                        <li>GAS / {NATIVE_ASSET}</li>
                        <li>STATUS: PRE-LAUNCH</li>
                    </ul>
                </div>

                <div>
                    <p className="label-tech mb-5">CONTRACT & EXTERNAL</p>
                    <ul className="space-y-3 font-mono text-[11px] uppercase tracking-[0.2em]">
                        <li className="text-mute">
                            CONTRACT:{" "}
                            {ACTRA_CONTRACT_ADDRESS ? (
                                <a
                                    href={EXPLORER_ADDRESS_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="footer-contract"
                                    title={ACTRA_CONTRACT_ADDRESS}
                                    className="text-[#31D8FF] transition-colors hover:text-ink"
                                >
                                    {CONTRACT_SHORT} ↗
                                </a>
                            ) : (
                                <span className="text-ink">COMING SOON</span>
                            )}
                        </li>
                        <li>
                            <a href="https://lift.fun" target="_blank" rel="noopener noreferrer" data-testid="footer-buy" className="inline-flex items-center gap-1.5 text-mute transition-colors hover:text-ink">
                                BUY — LIFT.FUN <ArrowUpRight size={11} />
                            </a>
                        </li>
                        <li>
                            <a href="https://dexscreener.com" target="_blank" rel="noopener noreferrer" data-testid="footer-chart" className="inline-flex items-center gap-1.5 text-mute transition-colors hover:text-ink">
                                CHART — DEXSCREENER <ArrowUpRight size={11} />
                            </a>
                        </li>
                        <li>
                            <a href={X_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-x" className="inline-flex items-center gap-1.5 text-mute transition-colors hover:text-ink">
                                SOCIAL — X <ArrowUpRight size={11} />
                            </a>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-white/[0.06] pt-8 md:flex-row md:items-center">
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-mute">
                    BUILT FOR THE ACTION ECONOMY.
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-mute/60">
                    {TAGLINE} — {PROJECT_NAME} / {CHAIN_ID}
                </p>
            </div>
        </div>
    </footer>
);

export default Footer;
